// @ts-nocheck
import {
  LoadingOpacityContainer,
  loadingOpacityMixin
} from "components/Loader/styled";
import useActiveWeb3React from "hooks/useActiveWeb3React";
import { Pair } from "lib/utils/pair";
import { darken } from "polished";
import { ReactNode, useEffect, useState } from "react";
import styled from "styled-components/macro";
import { formatCurrencyAmount } from "utils/formatCurrencyAmount";

import { Currency, CurrencyAmount, Percent, Token } from "@uniswap/sdk-core";

import { useSupportedTokens } from "../../hooks/Tokens";
import useTheme from "../../hooks/useTheme";
import { useCurrencyBalance } from "../../state/wallet/hooks";
import { ThemedText } from "../../theme";
import CurrencyLogo from "../CurrencyLogo";
import CurrencyDropdown from "../Dropdown/CurrencyDropdown";
import { Input as NumericalInput } from "../NumericalInput";
import { RowBetween, RowFixed } from "../Row";
import { FiatValue } from "./FiatValue";

const InputPanel = styled.div<{ hideInput?: boolean }>`
  ${({ theme }) => theme.flexColumnNoWrap}
  width: 100%;
  position: relative;
  border: none;
  border-radius: ${({ hideInput }) => (hideInput ? '16px' : '20px')};
  background-color: ${({ theme, hideInput }) => (hideInput ? 'transparent' : 'transparent')};
  z-index: 1;
  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};
  transition: height 1s ease;
  will-change: height;

`

const Container = styled.div<{ hideInput: boolean }>`
  border-radius: ${({ hideInput }) => (hideInput ? '16px' : '20px')};
  background-color: transparent;
  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};

  :focus,
  :hover {
  }
`

const InputRow = styled.div<{ selected: boolean }>`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  justify-content: space-between;
  padding: ${({ selected }) => (selected ? ' 1rem 1rem 0.75rem 1rem' : '1rem 1rem 1rem 1rem')};
  padding: 0px;
  padding-left: 0px;
  padding-top: 0px;
`

const LabelRow = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  color: ${({ theme }) => theme.text1};
  font-size: 0.75rem;
  line-height: 1rem;
  padding: 0 1rem 1rem;
  padding-left: 0px;

  span:hover {
    cursor: pointer;
    color: ${({ theme }) => darken(0.2, theme.text2)};
  }
`

const FiatRow = styled(LabelRow)`
  justify-content: flex-end;
`

const StyledNumericalInput = styled(NumericalInput)<{ $loading: boolean, disabled: boolean }>`
  ${loadingOpacityMixin};
  opacity: ${({ disabled }) => (!disabled ? 1 : 0.45)};
  text-align: left;
`

interface CurrencyInputPanelLimitedProps {
  value: string
  onUserInput: (value: string) => void
  onMax?: () => void
  showMaxButton: boolean
  label?: ReactNode
  onCurrencySelect?: (currency: Currency) => void
  currency?: Currency | null
  hideBalance?: boolean
  pair?: Pair | null
  hideInput?: boolean
  otherCurrency?: Currency | null
  fiatValue?: CurrencyAmount<Token> | null
  priceImpact?: Percent
  id: string
  showCommonBases?: boolean
  showCurrencyAmount?: boolean
  disableNonToken?: boolean
  renderBalance?: (amount: CurrencyAmount<Currency>) => ReactNode
  locked?: boolean
  loading?: boolean
  disabled?: boolean
  approved?: boolean
  maxBalance?: number
}

export default function CurrencyInputPanelLimited({
  value,
  onUserInput,
  onMax,
  showMaxButton,
  onCurrencySelect,
  currency,
  otherCurrency,
  id,
  showCommonBases,
  showCurrencyAmount,
  disableNonToken,
  renderBalance,
  fiatValue,
  priceImpact,
  hideBalance = false,
  pair = null, // used for double token logo
  hideInput = false,
  locked = false,
  loading = false,
  disabled = false,
  approved,
  maxBalance = -1,
  ...rest
}: CurrencyInputPanelLimitedProps) {
  const [selectedCurrency, setSelectedCurrency] = useState<Token>(null)
  const { account } = useActiveWeb3React()
  const selectedCurrencyBalance = useCurrencyBalance(account ?? undefined, selectedCurrency ?? undefined)
  const theme = useTheme()
  const allowedCurrencies = useSupportedTokens();

  useEffect(() => {
    // console.log(currency)
    if (currency) {
      setSelectedCurrency(currency)
    }
  }, [currency])

  return (
    <InputPanel id={id} hideInput={hideInput} {...rest} style={{ width: '100%' }}>
      <Container hideInput={hideInput}>
        <InputRow style={hideInput ? { padding: '0', borderRadius: '8px' } : {}} selected={!onCurrencySelect}>
          {!hideInput && (
            <StyledNumericalInput
              className='token-amount-input'
              value={value}
              onUserInput={onUserInput}
              maxBalance={formatCurrencyAmount(selectedCurrencyBalance, 6)}
              disabled={disabled}
              style={{marginRight: 10}}
              $loading={loading}
            />
          )}

          <CurrencyDropdown
            onUserClick={onCurrencySelect}
            disabled={disabled}
            selected={selectedCurrency}
            items={allowedCurrencies}
            style={approved?{opacity:'0.45'}:{opacity:'1'}}
          >
            { selectedCurrency == null ? 
                <></>
              :
              <>
                <CurrencyLogo style={{ marginRight: '0.5rem' }} currency={selectedCurrency} size={'32px'} />
              </>
            }
          </CurrencyDropdown>
        </InputRow>
        {!hideInput && !hideBalance && (
          <FiatRow>
            <RowBetween style={{ marginTop: '5px' }}>
              <LoadingOpacityContainer $loading={loading}>
                <FiatValue fiatValue={fiatValue} priceImpact={priceImpact} />
              </LoadingOpacityContainer>
              {account ? (
                <RowFixed style={{ height: '17px', width: '100%' }}>
                  <ThemedText.Body
                    onClick={() => {
                      if (disabled) {
                        return
                      }
                      onMax()
                    }}
                    color={theme.text3}
                    fontWeight={600}
                    fontSize={14}
                    style={{ display: 'inline', cursor: disabled ? 'auto' : 'pointer', opacity: (disabled ? "0.45" : 1) }}
                  >
                    {!hideBalance && currency && selectedCurrencyBalance
                      ? (
                        renderBalance ? (
                          renderBalance(selectedCurrencyBalance)
                        ) : (
                          <>Balance: {formatCurrencyAmount(selectedCurrencyBalance, 18, null, 6)}</>
                        )
                      )
                      :
                      <>Balance: 0</>
                    }
                  </ThemedText.Body>
                </RowFixed>
              ) : (
                <RowFixed style={{ height: '17px', width:'100%' }}>
                  <ThemedText.Body
                    color={theme.text3}
                    fontWeight={600}
                    fontSize={14}
                    style={{ display: 'inline', cursor: disabled ? 'auto' : 'pointer', opacity: (disabled ? "0.45" : 1) }}
                  >
                    <>Balance: 0</>
                  </ThemedText.Body>
                </RowFixed>
              )}
            </RowBetween>
          </FiatRow>
        )}
      </Container>
    </InputPanel>
  )
}
